import type { QueueColumn } from '@/components/designSystem/QueueViews/types'
import { formatDayMonthInOffset, formatHourMinuteInOffset } from '@/utils/date'
import type {
  PriorityOrderBy,
  PriorityPaymentRow
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { prioritySituationLabel } from './constants'
import { formatPriorityEligibilityCell } from './eligibilityFormat'
import { formatPlaceCell } from './placeFormat'
import { formatPriorityStationTime } from './rowFormat'
import {
  PRIORITY_SLA_STATE_LABELS,
  formatAntecipeStart,
  formatSlaDateTime,
  formatSlaKindLabel
} from './slaFormat'

/** `'21/09 09:47'` no fuso do plantão, ou `'—'` quando não há instante. */
const stamp = (iso: string | null, timezone: string): string => {
  if (!iso) return '—'
  const day = formatDayMonthInOffset(iso, timezone)
  const hour = formatHourMinuteInOffset(iso, timezone)
  return day && hour ? `${day} ${hour}` : '—'
}

/**
 * Coluna "Registro de Atividade": o que existe HOJE para aquele plantão.
 * "não precisa" e "coberto pela folha de ponto" não são ausência de registro —
 * são o motivo de o registro não ser cobrado.
 */
export const formatActivityRecordCell = (row: PriorityPaymentRow): string => {
  // `RESOLVED_OTHER` entra junto de `RESOLVED_APP`: o horário já está confirmado
  // por uma origem antiga, então não há registro a cobrar. Qual origem foi, o
  // selo da coluna Situação já diz ("Resolvido por outra origem").
  if (row.situation === 'RESOLVED_APP' || row.situation === 'RESOLVED_OTHER') return 'não precisa'
  if (row.situation === 'RESOLVED_SHEET') return 'coberto pela folha de ponto'

  const record = row.activityRecord
  if (!record || !record.submittedAt) return 'nenhum'

  const verb = record.rejectionCount > 1 ? 'reenviado' : 'enviado'
  const by = record.submittedByIsDoctor === false ? ' pela escalista' : ''
  return `${verb} ${stamp(record.submittedAt, row.timezone)}${by}`
}

/** Coluna "Auditoria": o veredito, ou a espera de quem ainda não foi julgado. */
export const formatAuditCell = (row: PriorityPaymentRow, serverNow: string): string => {
  if (row.situation === 'RESOLVED_SHEET') {
    return `folha aprovada ${stamp(row.lastActivityAt, row.timezone)}`
  }

  const record = row.activityRecord
  if (!record) return '—'

  if (record.status === 'APPROVED') return `aprovado ${stamp(record.reviewedAt, row.timezone)}`
  if (record.status === 'REJECTED') return `rejeitado ${stamp(record.reviewedAt, row.timezone)}`

  if (record.submittedAt) {
    const minutes = Math.max(
      0,
      Math.round((new Date(serverNow).getTime() - new Date(record.submittedAt).getTime()) / 60_000)
    )
    return minutes >= 60 ? `na fila há ${Math.floor(minutes / 60)} h` : `na fila há ${minutes} min`
  }

  return '—'
}

/**
 * Tabela plana, ordenada pelo prazo (M3). "Tempo" é a ordem PRIORITY do
 * servidor (`compareBySla`); "Plantão" continua `STATION_START`. Ordenar outra
 * coluna seria ordenar só os 25 da página.
 */
export const PRIORITY_SLA_SORT_KEY_TO_ORDER_BY: Record<string, PriorityOrderBy> = {
  time: 'PRIORITY',
  station: 'STATION_START'
}

export const formatAntecipeStartCell = (row: PriorityPaymentRow): string =>
  formatAntecipeStart(row) ?? '—'

/** Referência do "hoje" do rótulo do prazo; `null` se o `serverNow` for inválido. */
const serverNowMs = (serverNow: string): number | null => {
  const ms = Date.parse(serverNow)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Colunas do M3. As que têm marcação (tempo, estado, tipo · etapa, plantão,
 * situação) são preenchidas por slot em `PriorityPaymentsTable.vue`; o `value`
 * aqui é o texto simples de reserva.
 */
export const prioritySlaTableColumns = (
  serverNow: string,
  options: { eligibility?: boolean } = {}
): QueueColumn<PriorityPaymentRow>[] => {
  const columns: QueueColumn<PriorityPaymentRow>[] = [
    {
      key: 'time',
      label: 'Tempo',
      value: (row) => (row.sla ? formatSlaDateTime(row.sla.deadlineAt) : '—'),
      sortable: true,
      widthClass: 'w-36'
    },
    {
      key: 'state',
      label: 'Estado',
      value: (row) => (row.sla ? PRIORITY_SLA_STATE_LABELS[row.sla.state] : '—')
    },
    {
      key: 'sla-kind',
      label: 'Tipo de SLA · etapa',
      value: (row) => (row.sla ? formatSlaKindLabel(row.sla, row, serverNowMs(serverNow)) : '—')
    },
    { key: 'id', label: 'ID', value: (row) => String(row.doctorStationId), widthClass: 'w-24' },
    { key: 'doctor', label: 'Médico / Escala', value: (row) => row.doctorName },
    // Frente G (Q37): onde o plantão acontece, uma coluna por variável.
    { key: 'unit', label: 'Unidade', value: (row) => formatPlaceCell(row.healthUnitName) },
    { key: 'sector', label: 'Setor', value: (row) => formatPlaceCell(row.sectorName) },
    { key: 'position', label: 'Posição', value: (row) => formatPlaceCell(row.positionName) },
    {
      key: 'station',
      label: 'Plantão (hora local)',
      value: (row) => formatPriorityStationTime(row).text,
      sortable: true
    },
    { key: 'situation', label: 'Situação', value: (row) => prioritySituationLabel(row) },
    { key: 'record', label: 'Registro de Atividade', value: formatActivityRecordCell },
    { key: 'audit', label: 'Auditoria', value: (row) => formatAuditCell(row, serverNow) },
    { key: 'antecipe-start', label: 'Início do SLA (Antecipe)', value: formatAntecipeStartCell }
  ]
  // Frente B §6 — coluna "Elegibilidade" logo depois de "Situação", só com a flag;
  // sem ela, as colunas de hoje.
  if (!options.eligibility) return columns
  const situationIndex = columns.findIndex((column) => column.key === 'situation')
  columns.splice(situationIndex + 1, 0, {
    key: 'eligibility',
    label: 'Elegibilidade',
    value: formatPriorityEligibilityCell
  })
  return columns
}
